import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type LocaleCode = 'EUR_DE' | 'EUR_FR' | 'GBP_UK' | 'CHF_CH' | 'NOK_NO' | 'SEK_SE' | 'USD_US'

export interface LocaleSpec {
  code: LocaleCode
  label: string
  flag: string
  bcp47: string          // Intl.NumberFormat locale
  currency: string       // ISO 4217
  hint?: string          // surfaced as a tooltip when active
}

export const LOCALES: Record<LocaleCode, LocaleSpec> = {
  EUR_DE: { code: 'EUR_DE', label: 'Germany',     flag: '🇩🇪', bcp47: 'de-DE', currency: 'EUR', hint: 'Bildungsgutschein, Hundesteuer, Krankenkasse.' },
  EUR_FR: { code: 'EUR_FR', label: 'France',      flag: '🇫🇷', bcp47: 'fr-FR', currency: 'EUR', hint: 'Sécurité sociale + CSG sont déjà inclus.' },
  GBP_UK: { code: 'GBP_UK', label: 'UK',          flag: '🇬🇧', bcp47: 'en-GB', currency: 'GBP', hint: 'NI + PAYE assumed; ISA savings count separately.' },
  CHF_CH: { code: 'CHF_CH', label: 'Switzerland', flag: '🇨🇭', bcp47: 'de-CH', currency: 'CHF', hint: 'Pillar-3a tax shield not modelled.' },
  NOK_NO: { code: 'NOK_NO', label: 'Norway',      flag: '🇳🇴', bcp47: 'nb-NO', currency: 'NOK', hint: 'Skatteetaten brackets ignored; values are gross.' },
  SEK_SE: { code: 'SEK_SE', label: 'Sweden',      flag: '🇸🇪', bcp47: 'sv-SE', currency: 'SEK', hint: 'Skatteverket brackets ignored; values are gross.' },
  USD_US: { code: 'USD_US', label: 'USA',         flag: '🇺🇸', bcp47: 'en-US', currency: 'USD', hint: '401(k) match + state tax not modelled.' },
}

/**
 * Per-locale calibration multipliers applied to EUR-baseline template values
 * when a user creates a decision under that locale. Not pure FX — these
 * approximate native-currency salary realism so seed templates feel right.
 *
 * E.g. €52K (DE baseline) →
 *   £45K (UK, 0.87×), CHF 75K (CH, 1.45×), 600K NOK (NO, 11.5×), $90K (US, 1.75×)
 */
export const LOCALE_CALIBRATION: Record<LocaleCode, number> = {
  EUR_DE: 1.00,
  EUR_FR: 0.95,
  GBP_UK: 0.87,
  CHF_CH: 1.45,
  NOK_NO: 11.50,
  SEK_SE: 10.50,
  USD_US: 1.75,
}

/** Infer the best LocaleCode from navigator.language (BCP-47 tag). */
export function detectLocale(): LocaleCode {
  const lang = (typeof navigator !== 'undefined' ? navigator.language : '') || ''
  const l = lang.toLowerCase()
  if (l.startsWith('de')) return 'EUR_DE'
  if (l.startsWith('fr')) return 'EUR_FR'
  if (l === 'en-gb' || l.startsWith('en-gb')) return 'GBP_UK'
  if (l.startsWith('de-ch') || l.startsWith('fr-ch') || l.startsWith('it-ch')) return 'CHF_CH'
  if (l.startsWith('nb') || l.startsWith('nn') || l.startsWith('no')) return 'NOK_NO'
  if (l.startsWith('sv')) return 'SEK_SE'
  if (l.startsWith('en-us') || l === 'en') return 'USD_US'
  return 'EUR_DE'
}

interface LocaleState {
  code: LocaleCode
  _hydrated: boolean   // true once zustand has rehydrated from localStorage
  setCode: (c: LocaleCode) => void
  // FX rates from EUR base (refreshed daily server-side)
  fxRates: Record<string, number>  // e.g. { EUR: 1, GBP: 0.84, USD: 1.08 }
  fxAsOf: string | null
  fxFetched: boolean
  setFx: (rates: Record<string, number>, asOf: string) => void
}

export const useLocale = create<LocaleState>()(
  persist(
    (set) => ({
      code: detectLocale(),   // default — overwritten by persisted value on rehydration
      _hydrated: false,
      setCode: (code) => set({ code }),
      fxRates: { EUR: 1, GBP: 0.84, CHF: 0.95, NOK: 11.30, SEK: 11.10, USD: 1.08 },
      fxAsOf: null,
      fxFetched: false,
      setFx: (rates, asOf) => set({ fxRates: rates, fxAsOf: asOf, fxFetched: true }),
    }),
    {
      name: 'lifeodds-locale',
      onRehydrateStorage: () => (state) => {
        if (state) state._hydrated = true
      },
    },
  ),
)

export function currentLocale(): LocaleSpec {
  return LOCALES[useLocale.getState().code]
}

/** Module-level "active base currency" used by formatCurrency when no
 * explicit baseCurrency is passed. Pages that have a decision in scope set
 * this on mount; defaults to 'EUR' for backwards compatibility. */
let _activeBase = 'EUR'
export function setActiveBaseCurrency(ccy: string | undefined | null) {
  _activeBase = (ccy && ccy.toUpperCase()) || 'EUR'
}
export function getActiveBaseCurrency(): string {
  return _activeBase
}

/** Convert an amount expressed in the BASE currency (default EUR) into the
 * user's currently selected display currency, using the cached FX rates.
 * If rates haven't loaded yet, falls back to the persisted defaults. */
export function convertFromBase(amount: number, baseCurrency: string = 'EUR'): number {
  if (!isFinite(amount)) return amount
  const { fxRates, code } = useLocale.getState()
  const target = LOCALES[code].currency
  if (baseCurrency === target) return amount
  const baseRate = fxRates[baseCurrency]
  const targetRate = fxRates[target]
  if (!baseRate || !targetRate) return amount
  // fxRates are quoted vs EUR. Convert amount to EUR first, then to target.
  const inEur = baseCurrency === 'EUR' ? amount : amount / baseRate
  return baseCurrency === 'EUR' ? amount * targetRate : inEur * targetRate
}

/** Inverse of convertFromBase: a number the user typed in their locale's
 *  display currency, converted into the decision's base currency so it can be
 *  compared against raw model values. */
export function convertToBase(amount: number, baseCurrency: string = 'EUR'): number {
  if (!isFinite(amount)) return amount
  const { fxRates, code } = useLocale.getState()
  const source = LOCALES[code].currency
  if (baseCurrency === source) return amount
  const sourceRate = fxRates[source]
  const baseRate = fxRates[baseCurrency]
  if (!sourceRate || !baseRate) return amount
  const inEur = source === 'EUR' ? amount : amount / sourceRate
  return baseCurrency === 'EUR' ? inEur : inEur * baseRate
}
